/**
 * Component Demo Page
 *
 * Demonstrates all Phase 5 feedback components:
 * - Skeleton loaders
 * - Empty states
 * - Success screens
 * - Toast notifications
 * - Loading buttons
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Skeleton, CardSkeleton, TableSkeleton } from '../../components/common/Skeleton';
import { EmptyState } from '../../components/common/EmptyState';
import { SuccessScreen } from '../../components/feedback/SuccessScreen';
import { useToastHelpers } from '../../components/feedback';
import { Inbox, Plus, RefreshCw, CheckCircle2, Sparkles } from 'lucide-react';

export const ComponentDemoPage: React.FC = () => {
  const navigate = useNavigate();
  const { success, error, warning, info } = useToastHelpers();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showEmpty, setShowEmpty] = useState(true);

  const handleAsyncAction = async () => {
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsLoading(false);
    success('Action completed successfully!', 'Success');
  };

  const handleShowSuccess = () => {
    setShowSuccess(true);
  };

  const handleToggleEmpty = () => {
    setShowEmpty(!showEmpty);
  };

  if (showSuccess) {
    return (
      <PageLayout
        pageTitle="Success!"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Component Demo' },
        ]}
      >
        <SuccessScreen
          title="Operation Completed!"
          message="Your action has been successfully completed. You can now continue with your workflow."
          primaryAction={{
            label: 'Continue',
            onClick: () => setShowSuccess(false),
          }}
          secondaryAction={{
            label: 'Go Home',
            onClick: () => navigate('/'),
          }}
          showAnimation={true}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle="Phase 5 Component Demo"
      breadcrumbs={[
        { label: 'Home', href: '/' },
        { label: 'Component Demo' },
      ]}
    >
      <div className="space-y-6">
        {/* Toast Notifications Demo */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">Toast Notifications</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Click buttons to trigger different toast notifications (top-right corner)
            </p>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="primary"
                onClick={() => success('Your changes have been saved', 'Success')}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Success Toast
              </Button>
              <Button
                variant="danger"
                onClick={() => error('Failed to complete the operation', 'Error')}
              >
                Error Toast
              </Button>
              <Button
                variant="secondary"
                onClick={() => warning('Please review your input', 'Warning')}
              >
                Warning Toast
              </Button>
              <Button
                variant="outline"
                onClick={() => info('New features are available', 'Information')}
              >
                Info Toast
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Loading States Demo */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">Loading States</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Button with loading spinner and skeleton loaders
            </p>
          </CardHeader>
          <CardBody>
            <div className="space-y-6">
              {/* Loading Button */}
              <div>
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Loading Button</h3>
                <Button
                  variant="primary"
                  size="lg"
                  isLoading={isLoading}
                  onClick={handleAsyncAction}
                >
                  {isLoading ? 'Processing...' : 'Start Async Action'}
                </Button>
              </div>

              {/* Skeleton Loaders */}
              <div>
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Skeleton Loaders</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-neutral-500 mb-2">Text Skeleton</p>
                    <Skeleton width="w-3/4" height="h-6" />
                    <Skeleton width="w-full" height="h-4" className="mt-2" />
                    <Skeleton width="w-5/6" height="h-4" className="mt-2" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 mb-2">Circular Skeleton</p>
                    <div className="flex items-center gap-3">
                      <Skeleton variant="circular" width="w-12" height="h-12" />
                      <div className="flex-1">
                        <Skeleton width="w-1/2" height="h-4" />
                        <Skeleton width="w-3/4" height="h-3" className="mt-2" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Skeleton */}
              <div>
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Card Skeleton</h3>
                <CardSkeleton />
              </div>

              {/* Table Skeleton */}
              <div>
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Table Skeleton</h3>
                <TableSkeleton rows={3} />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Empty State Demo */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">Empty States</h2>
            <p className="text-sm text-neutral-600 mt-1">
              User-friendly empty states with clear call-to-action
            </p>
          </CardHeader>
          <CardBody>
            <Button
              variant="outline"
              onClick={handleToggleEmpty}
              className="mb-4"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Toggle Empty State
            </Button>

            {showEmpty ? (
              <EmptyState
                icon={<Inbox className="w-10 h-10" />}
                title="No items found"
                description="Get started by creating your first item. You can add new items anytime."
                action={{
                  label: 'Create New Item',
                  onClick: () => {
                    info('Create new item action clicked');
                    setShowEmpty(false);
                  },
                  icon: <Plus className="w-5 h-5 mr-2" />,
                }}
                secondaryAction={{
                  label: 'Learn More',
                  onClick: () => info('Learn more clicked'),
                }}
              />
            ) : (
              <div className="py-8 text-center text-neutral-600">
                <p>Content would be displayed here...</p>
                <Button
                  variant="ghost"
                  onClick={handleToggleEmpty}
                  className="mt-4"
                >
                  Show Empty State Again
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Success Screen Demo */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">Success Screen</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Celebratory success screen with animations
            </p>
          </CardHeader>
          <CardBody>
            <Button
              variant="primary"
              size="lg"
              onClick={handleShowSuccess}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Show Success Screen
            </Button>
            <p className="text-sm text-neutral-600 mt-3">
              This will display a full-screen success message with animated checkmark
            </p>
          </CardBody>
        </Card>

        {/* Animation Examples */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">Animations</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Custom Tailwind animations added to the theme
            </p>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="mb-3 flex items-center justify-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center animate-scale-in">
                    <CheckCircle2 className="w-8 h-8 text-primary-600" />
                  </div>
                </div>
                <p className="text-sm font-medium">scale-in</p>
                <p className="text-xs text-neutral-500">Used in success screens</p>
              </div>

              <div className="text-center">
                <div className="mb-3 animate-fade-in-up">
                  <div className="w-16 h-16 bg-success-100 rounded-xl flex items-center justify-center mx-auto">
                    <Sparkles className="w-8 h-8 text-success-600" />
                  </div>
                </div>
                <p className="text-sm font-medium">fade-in-up</p>
                <p className="text-xs text-neutral-500">Used for content reveal</p>
              </div>

              <div className="text-center">
                <div className="mb-3 flex items-center justify-center">
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 bg-info-100 rounded-full animate-ping-slow opacity-20" />
                    <div className="relative w-full h-full bg-info-100 rounded-full flex items-center justify-center">
                      <RefreshCw className="w-8 h-8 text-info-600" />
                    </div>
                  </div>
                </div>
                <p className="text-sm font-medium">ping-slow</p>
                <p className="text-xs text-neutral-500">Attention grabber</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Back Button */}
        <div className="flex justify-center pt-6">
          <Button variant="outline" onClick={() => navigate('/')}>
            ← Back to Home
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};

export default ComponentDemoPage;
